import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface AuthState {
  token: string | null;
  username: string | null;
  menuList: string[];
  permissionList: string[];
  setSession: (token: string, username?: string, permissionList?: string[], menuList?: string[]) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      username: null,
      menuList: [],
      permissionList: [],
      setSession: (token, username, permissionList, menuList) =>
        set({
          token,
          username: username ?? null,
          permissionList: permissionList ?? [],
          menuList: menuList ?? [],
        }),
      clear: () => set({ token: null, username: null, permissionList: [], menuList: [] }),
    }),
    {
      name: 'ads-admin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        username: state.username,
      }),
    },
  ),
);

let hydrationPromise: Promise<void> | null = null;

/**
 * Wait until zustand/persist has restored auth state from localStorage.
 * Route guards MUST await this before reading `token` to avoid a first-frame redirect
 * loop on cold load or environments with slow storage polyfills.
 */
export function waitForAuthHydration(): Promise<void> {
  if (useAuthStore.persist.hasHydrated()) return Promise.resolve();
  if (!hydrationPromise) {
    hydrationPromise = new Promise<void>((resolve) => {
      const unsub = useAuthStore.persist.onFinishHydration(() => {
        unsub();
        resolve();
      });
    });
  }
  return hydrationPromise;
}
